import type { ReactNode } from "react";
import { useLocation } from "wouter";
import {
  BarChart3,
  BookOpen,
  Home,
} from "lucide-react";
import { StudentHeader } from "./StudentHeader";
import { STUDENT_COLORS, STUDENT_FONT } from "./studentTheme";

export type StudentTab =
  "home" | "learning" | "scores" | "settings" | "profile";

const TABS: Array<{
  key: StudentTab;
  label: string;
  path: string;
  icon: typeof Home;
}> = [
  { key: "home", label: "ホーム", path: "/mypage/home", icon: Home },
  { key: "learning", label: "学習", path: "/mypage/learning", icon: BookOpen },
  {
    key: "scores",
    label: "学習スコア",
    path: "/mypage/scores",
    icon: BarChart3,
  },
];

export { STUDENT_COLORS, STUDENT_FONT } from "./studentTheme";

export function StudentMobileShell({
  active,
  eyebrow,
  title,
  children,
}: {
  active: StudentTab;
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  const [, setLocation] = useLocation();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: STUDENT_COLORS.bg,
        color: STUDENT_COLORS.ink,
        fontFamily: STUDENT_FONT,
      }}
    >
      <StudentHeader
        maxWidth={760}
        onLogoClick={() => setLocation("/mypage/home")}
        srLabel={`${eyebrow} ${title}`}
        profileCurrent={active === "profile" || active === "settings"}
        onProfileClick={() => setLocation("/mypage/profile")}
      />

      <main
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            padding: "16px 14px 112px",
          }}
        >
          {children}
        </div>
      </main>

      <nav
        aria-label="学生用メニュー"
        style={{
          position: "absolute",
          zIndex: 100,
          left: 0,
          right: 0,
          bottom: 0,
          background: STUDENT_COLORS.surface,
          borderTop: `1px solid ${STUDENT_COLORS.line}`,
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            paddingBottom: "max(0px, env(safe-area-inset-bottom))",
          }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const selected = tab.key === active;
            return (
              <button
                key={tab.key}
                type="button"
                aria-current={selected ? "page" : undefined}
                aria-label={tab.label}
                onClick={() => setLocation(tab.path)}
                style={{
                  minWidth: 0,
                  border: 0,
                  borderTop: selected
                    ? `3px solid ${STUDENT_COLORS.cyan}`
                    : "3px solid transparent",
                  background: "transparent",
                  color: selected ? STUDENT_COLORS.cyan : STUDENT_COLORS.ink,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  padding: "9px 2px 11px",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  fontSize: 10,
                  fontWeight: 800,
                }}
              >
                <Icon size={17} />
                <span
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
